import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useFeedback } from '@/components/feedback/feedback-context'
import { FormField } from '@/components/forms/form-field'
import { Input } from '@/components/forms/input'
import { Modal } from '@/components/overlays/modal'
import { Button } from '@/components/primitives/button'

import { createAdminAccount, type AdminAccountCreateResult } from './api'

interface AdminAccountCreateModalProps {
  onClose: () => void
  onCreated: (admin: AdminAccountCreateResult) => void
  open: boolean
}

type CreateAdminErrors = Partial<Record<'email' | 'name', string>>

const emailPattern = /^[\w.!#$%&'*+\-/=?^{|}~]+@([\w-]+\.)+[\w-]{2,}$/i

export function AdminAccountCreateModal({
  onClose,
  onCreated,
  open,
}: AdminAccountCreateModalProps) {
  const { t } = useTranslation('adminSettings')
  const { showFeedback } = useFeedback()
  const queryClient = useQueryClient()
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [mustChangePassword, setMustChangePassword] = useState(true)
  const [errors, setErrors] = useState<CreateAdminErrors>({})

  const createMutation = useMutation({
    mutationFn: async () => {
      const nextErrors: CreateAdminErrors = {}
      if (!name.trim()) {
        nextErrors.name = t('adminAccounts.createModal.validation.nameRequired')
      }
      if (!email.trim()) {
        nextErrors.email = t('adminAccounts.createModal.validation.emailRequired')
      } else if (!emailPattern.test(email.trim())) {
        nextErrors.email = t('adminAccounts.createModal.validation.emailInvalid')
      }
      if (Object.keys(nextErrors).length > 0) {
        setErrors(nextErrors)
        return undefined
      }

      setErrors({})
      return createAdminAccount({ email, mustChangePassword, name })
    },
    onError: () => {
      showFeedback({
        message: t('errors.submitFailed'),
        tone: 'error',
      })
    },
    onSuccess: async (admin) => {
      if (!admin) {
        return
      }

      await queryClient.invalidateQueries({ queryKey: ['admin-settings-admin-accounts'] })
      showFeedback({
        message: t('success.createAdminAccount'),
        tone: 'success',
      })
      setName('')
      setEmail('')
      setMustChangePassword(true)
      onClose()
      onCreated(admin)
    },
  })

  return (
    <Modal
      closeLabel={t('adminAccounts.createModal.close')}
      footer={
        <>
          <Button
            disabled={createMutation.isPending}
            onClick={onClose}
            size="sm"
            variant="secondary"
          >
            {t('actions.cancel')}
          </Button>
          <Button
            form="admin-account-create-form"
            isLoading={createMutation.isPending}
            size="sm"
            type="submit"
          >
            {t('adminAccounts.createModal.confirm')}
          </Button>
        </>
      }
      onClose={onClose}
      open={open}
      title={t('adminAccounts.createModal.title')}
    >
      <form
        className="grid gap-4"
        id="admin-account-create-form"
        onSubmit={(event) => {
          event.preventDefault()
          createMutation.mutate()
        }}
      >
        <FormField
          error={errors.name}
          htmlFor="admin-account-create-name"
          label={t('adminAccounts.createModal.name')}
          required
        >
          <Input
            autoComplete="name"
            id="admin-account-create-name"
            invalid={Boolean(errors.name)}
            onChange={(event) => {
              setName(event.target.value)
              setErrors((current) => ({ ...current, name: undefined }))
            }}
            placeholder={t('adminAccounts.createModal.namePlaceholder')}
            value={name}
          />
        </FormField>
        <FormField
          error={errors.email}
          htmlFor="admin-account-create-email"
          label={t('adminAccounts.createModal.email')}
          required
        >
          <Input
            autoComplete="email"
            id="admin-account-create-email"
            invalid={Boolean(errors.email)}
            onChange={(event) => {
              setEmail(event.target.value)
              setErrors((current) => ({ ...current, email: undefined }))
            }}
            placeholder={t('adminAccounts.createModal.emailPlaceholder')}
            type="email"
            value={email}
          />
        </FormField>
        <label className="inline-flex cursor-pointer items-center gap-2 text-sm text-[var(--color-text)]">
          <input
            checked={mustChangePassword}
            className="size-3.5 accent-[var(--color-primary)]"
            onChange={(event) => setMustChangePassword(event.target.checked)}
            type="checkbox"
          />
          {t('adminAccounts.createModal.mustChangePassword')}
        </label>
      </form>
    </Modal>
  )
}
